import { useEffect, useState } from "react";

import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Image,
  ProgressBar,
  Spinner,
  CloseButton,
} from "react-bootstrap";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
// import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import API from "../../api/api";

// ---- Yup Validation ----
const FILE_SIZE_MB = 2;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

function BlogsModal({
  show,
  handleClose,
  payload,
  setPayload,
  getAllData,
  hostname,
}) {

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"], // 👈 enable image
    ],
  };


  // console.log(payload);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (payload?.cover_img) {
      setPreview(payload.cover_img);
    }
  }, [payload]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const fd = new FormData();
      Object.entries(values).forEach(([k, v]) => {
        if (k === "cover_img") {
          if (v instanceof File) fd.append(k, v);
        } else {
          fd.append(k, v);
        }
      });

      const url = `/api/blogs/create-blog`;

      const method = API.post;

      await method(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });

      resetForm();
      setPreview(null);
      setProgress(0);
      handleClose();
      getAllData();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteEntry = () => {
    API
      .post(`/api/blogs/delete-blog`, {
        testimonial_id: payload?._id,
      })
      .then((res) => {
        // console.log(res.data);
        getAllData();
        handleClose();
        toast.warning("Blog deleted.", { theme: "colored" });
        setPayload({});
      })
      .catch((err) => {
        console.log(err);
        getAllData();
        if ("response" in err)
          toast.error(err.response.data.error, { theme: "colored" });
        else toast.error("something went wrong!", { theme: "colored" });
      });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header>
        <Modal.Title>{payload.loadType}</Modal.Title>
        <CloseButton onClick={handleClose} />
      </Modal.Header>
      <Modal.Body>
        {payload.loadType === "Add Blog" && (
          <Formik
            initialValues={{
              title: "",
              slug: "",
              summary: "",
              content: "",
              status: "draft",
              order: "",
              tags: "",
              cover_img: null,
            }}
            validationSchema={Yup.object({
              title: Yup.string().required("Title is required"),
              summary: Yup.string().required("Summary is required").max(300),
              slug: Yup.string().optional(),
              content: Yup.string().required("Content is required"),
              status: Yup.string().oneOf(["draft", "published"]).required(),
              order: Yup.number().integer().min(0).optional(),
              tags: Yup.string().optional(),
              cover_img: Yup.mixed()
                .test(
                  "fileSize",
                  `Must be ≤ ${FILE_SIZE_MB}MB`,
                  (file) =>
                    !file || (file && file.size <= FILE_SIZE_MB * 1024 * 1024)
                )
                .test(
                  "fileType",
                  "Invalid file type",
                  (file) => !file || ACCEPTED_TYPES.includes(file?.type)
                ),
            })}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, values, isSubmitting }) => (
              <FormikForm>
                <Modal.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label>Title *</Form.Label>
                      <Field name="title" as={Form.Control} />
                      <ErrorMessage
                        name="title"
                        component="div"
                        className="text-danger fs--1"
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label>Slug</Form.Label>
                      <Field name="slug" as={Form.Control} />
                      <ErrorMessage
                        name="slug"
                        component="div"
                        className="text-danger fs--1"
                      />
                    </Col>
                    <Col md={12}>
                      <Form.Label>Summary *</Form.Label>
                      <Field
                        name="summary"
                        as={Form.Control}
                        asElement="textarea"
                        rows={2}
                      />
                      <ErrorMessage
                        name="summary"
                        component="div"
                        className="text-danger fs--1"
                      />
                    </Col>
                    <Col md={12}>
                      <Form.Label>Content *</Form.Label>
                      <ReactQuill
                        value={values.content}
                        onChange={(val) => setFieldValue("content", val)}
                        theme="snow"
                        style={{ height: "100px", marginBottom: "40px" }} // 👈 change height here
                        modules={modules}
                      />
                      <ErrorMessage
                        name="content"
                        component="div"
                        className="text-danger fs--1"
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Label>Status *</Form.Label>
                      <Field name="status" as={Form.Select}>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </Field>
                      <ErrorMessage
                        name="status"
                        component="div"
                        className="text-danger fs--1"
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>Order</Form.Label>
                      <Field name="order" as={Form.Control} type="number" />
                    </Col>
                    <Col md={6}>
                      <Form.Label>Tags</Form.Label>
                      <Field name="tags" as={Form.Control} />
                    </Col>
                    <Col md={12}>
                      <Form.Label>Cover Image</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.currentTarget.files?.[0];
                          setFieldValue("cover_img", file);
                          if (file) setPreview(URL.createObjectURL(file));
                        }}
                      />
                      <ErrorMessage
                        name="cover_img"
                        component="div"
                        className="text-danger fs--1"
                      />
                      {preview && (
                        <Image
                          src={preview}
                          className="mt-2"
                          thumbnail
                          style={{ maxHeight: 150 }}
                        />
                      )}
                    </Col>
                    {isSubmitting && (
                      <Col md={12}>
                        <ProgressBar
                          now={progress}
                          label={`${progress}%`}
                          animated
                        />
                      </Col>
                    )}
                  </Row>
                  <div className="text-end">
                    <br />
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Spinner
                            size="sm"
                            animation="border"
                            className="me-2"
                          />{" "}
                          Saving…
                        </>
                      ) : (
                        "Create"
                      )}
                    </Button>
                  </div>
                </Modal.Body>
              </FormikForm>
            )}
          </Formik>
        )}

        {/* {payload.loadType === "Update Blog" && <div></div>} */}

        {payload.loadType === "Delete Blog" && (
          <div>
            <>
              <p className="text-danger">
                Are you sure you want to delete this Blog?
                <span className="text-primary">"{payload.title}"</span>?
              </p>
              <div className="text-end">
                <Button
                  variant="danger"
                  className="me-3 "
                  size="md"
                  onClick={deleteEntry}
                  style={{ width: 80 }}
                >
                  Yes
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleClose}
                  style={{ width: 80 }}
                >
                  No
                </Button>
              </div>
            </>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default BlogsModal;
